import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { TokenHelper } from '../../TokenHelper';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces/Customer';
import { dataActionCreators, Invoice } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { DataHelpers } from '../../DataHelpers';
import { useEditorStyles, useStackClassName } from '../Styles/Editor';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Link, MessageBar, MessageBarBody, MessageBarIntent, MessageBarTitle, Text, Toast, ToastBody, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { FileViewer } from '../FileViewer/FileViewer';
import { DownloadFile } from '../Tools/FileDownloadHelper';
import { useCustomers, useRigths } from '../Tools/ObjectLoader';
import { DigitealValidateDocumentResp, DigitealValidateDocument, DigitealValidateDocument_ValidationResultStatus } from '../../API/Digiteal';
import { AnalysePeppolXML } from '../../Peppol/Peppol';
import { DecodeDoc } from '../../Peppol/PeppolInterfaces';
import { SupportedDocuments, SupportedDocument } from '../../API/PeppolDirectory';
import { SortHelper } from '../../SortHelper';

function ValidationToMessageBarIntent(s: DigitealValidateDocument_ValidationResultStatus): MessageBarIntent {
    switch (s) {
        case "OK": return "success";
        case "WARNING": return "warning";
        case "ERROR": return "error";
    }
    return "error";
}

export interface InvoiceByPeppolDTO {
    invoiceid: string,
    sendToProd: boolean,
}

export const InvoicesSendUBL = () => {
    // All data needed for edition should be loaded before calling the editor
    const invoiceSendUBL = useSelector<ApplicationState, Invoice | null>((state: ApplicationState) => state.data.invoiceSendUBL);
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { customers } = useCustomers();
    const { isAdmin } = useRigths();

    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t, i18n } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    const viewDebugInfo = useSelector((state: ApplicationState) => state.userNeomyticLogin.viewDebugInfo);
    // Fields
    const [c, setC] = React.useState<Customer | null>(null);
    // Form validation
    const [file, setFile] = React.useState<Blob>();
    const [decodeDoc, setDecodeDoc] = React.useState<DecodeDoc | null>(null);
    const [UBLValidation, setUBLValidation] = React.useState(false);
    const [UBLValidate, setUBLValidate] = React.useState<DigitealValidateDocumentResp | null>();
    const [customerSupportedDocumentProd, setCustomerSupportedDocumentProd] = React.useState<SupportedDocuments | null | undefined>(null);
    const [customerSupportedDocumentTest, setCustomerSupportedDocumentTest] = React.useState<SupportedDocuments | null | undefined>(null);

    React.useEffect(() => {
        const c = invoiceSendUBL ? DataHelpers.GetCustomerFromId(customers, invoiceSendUBL.customerid) : null;
        setC(c);
    }, [invoiceSendUBL, customers]);

    React.useEffect(() => {
        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null && invoiceSendUBL && invoiceSendUBL.invoiceid) {
                    DownloadFile('api/Invoices/' + invoiceSendUBL.invoiceid + "/GenUBLInvoice", token)
                        .then((f) => {
                            if (f) {
                                setFile(f);
                                f.text().then((s) => {
                                    const decodeDoc = AnalysePeppolXML(s);
                                    setDecodeDoc(decodeDoc);
                                });
                            }
                        })
                        .catch((e) => { console.log(e); });
                }
            });
    }, []);

    React.useEffect(() => {
        const load = async () => {
            setUBLValidation(true);
            const token = await TokenHelper.GetToken(instance, teamsState);
            if (token !== null && invoiceSendUBL && invoiceSendUBL.invoiceid) {
                const res = await DigitealValidateDocument(token, invoiceSendUBL.invoiceid);
                setUBLValidate(res);
            }
            setUBLValidation(false);
        };
        load();
    }, []);

    React.useEffect(() => {
        if (decodeDoc) {
            const peppolId = decodeDoc.customerEndpointID;
            const load = async () => {
                setUBLValidation(true);
                const token = await TokenHelper.GetToken(instance, teamsState);
                if (peppolId) {
                    if (isAdmin) {
                        const res = await SupportedDocument(token, peppolId, "Test");
                        setCustomerSupportedDocumentTest(res);
                    }
                    const res2 = await SupportedDocument(token, peppolId, "Prod");
                    setCustomerSupportedDocumentProd(res2);
                }
                setUBLValidation(false);
            };
            load();
        }
    }, [decodeDoc, isAdmin]);

    const submit = (Test: boolean) => {
        if (invoiceSendUBL && invoiceSendUBL.invoiceid) {
            const d: InvoiceByPeppolDTO = {
                invoiceid: invoiceSendUBL.invoiceid,
                sendToProd: !Test,
            };
            TokenHelper.GetToken(instance, teamsState)
                .then(token => {
                    if (token !== null) {
                        const toastId = (Math.random() + 1).toString(36).substring(7);
                        dispatchToast(
                            <Toast appearance="inverted">
                                <ToastTitle>
                                    {i18n.t('SendingPeppol', { ns: "Toasts" })}
                                </ToastTitle>
                                <ToastBody>
                                    <div className={styles.Vertical}>
                                        <Text>{t('Customer') + " " + (c ? c.displayName : "?")}</Text>
                                        <Text>{"Ref " + (invoiceSendUBL.invoiceRef ? invoiceSendUBL.invoiceRef : "?")}</Text>
                                    </div>
                                </ToastBody>
                            </Toast>,
                            { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                        );
                        fetch("api/Invoices/" + invoiceSendUBL.invoiceid + "/SendInvoiceByPeppol", {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                authorization: "bearer " + token,
                            },
                            body: JSON.stringify(d),
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 200) {
                                    updateToast({
                                        content: (
                                            <Toast appearance="inverted">
                                                <ToastTitle action={
                                                    <ToastTrigger>
                                                        <Link>Dismiss</Link>
                                                    </ToastTrigger>
                                                }>
                                                    {i18n.t('SendingPeppolSucceed', { ns: "Toasts" })}
                                                </ToastTitle>
                                                <ToastBody>
                                                    <div className={styles.Vertical}>
                                                        <Text>{t('Customer') + " " + (c ? c.displayName : "?")}</Text>
                                                        <Text>{"Ref " + (invoiceSendUBL.invoiceRef ? invoiceSendUBL.invoiceRef : "?")}</Text>
                                                    </div>
                                                </ToastBody>
                                            </Toast>
                                        ),
                                        intent: "success",
                                        toastId,
                                        timeout: 5000,
                                    });
                                    if (!Test) dispatch(dataActionCreators.requestInvoiceSendUBL(null));
                                }
                                else {
                                    updateToast({
                                        content: (
                                            <Toast appearance="inverted">
                                                <ToastTitle action={
                                                    <ToastTrigger>
                                                        <Link>Dismiss</Link>
                                                    </ToastTrigger>
                                                }>
                                                    {i18n.t('SendingPeppolFailed', { ns: "Toasts" })}
                                                </ToastTitle>
                                                <ToastBody>
                                                    <div className={styles.Vertical}>
                                                        <Text>{t('Customer') + " " + (c ? c.displayName : "?")}</Text>
                                                        <Text>{"Ref " + (invoiceSendUBL.invoiceRef ? invoiceSendUBL.invoiceRef : "?")}</Text>
                                                    </div>
                                                </ToastBody>
                                            </Toast>
                                        ),
                                        intent: "error",
                                        toastId,
                                        timeout: 10000,
                                    });
                                }
                            });
                    }
                });
        }
    };
    return <Dialog
        open
        modalType="modal">
        <DialogSurface className={styles.DialogContentWithFilePreview}>
            <DialogBody className={styles.fluid}>
                <DialogTitle>{"Email invoice " + (invoiceSendUBL ? invoiceSendUBL.invoiceRef : "") + (invoiceSendUBL && invoiceSendUBL.emailSent ? " (" + t('EmailedDate') + " " + new Date(invoiceSendUBL.emailSent + "Z").toLocaleString() + ")" : "")}</DialogTitle>
                <DialogContent>
                    <div className={styles.onSingleLinWithFilePreviewe}>
                        <FileViewer file={file} />
                        <div className={styles.InfoPart}>
                            <form
                                onSubmit={() => {
                                    dispatch(dataActionCreators.requestInvoiceSendUBL(null));
                                }}
                                className={useStackClassName()}
                                autoComplete="off"
                            >
                                {decodeDoc && decodeDoc.customerName &&
                                    (customerSupportedDocumentProd === null
                                        ?
                                        <MessageBar intent="info">
                                            <MessageBarBody>
                                                <MessageBarTitle>{decodeDoc.customerName} Can receive invoices check</MessageBarTitle>
                                                In progress
                                            </MessageBarBody>
                                        </MessageBar>
                                        :
                                        <MessageBar intent={customerSupportedDocumentProd && customerSupportedDocumentProd.canReceiveInvoices ? "success" : "error"} >
                                            <MessageBarBody>
                                                <MessageBarTitle>{decodeDoc.customerName} Can receive invoices check</MessageBarTitle>
                                            </MessageBarBody>
                                        </MessageBar>
                                    )
                                }
                                {isAdmin && decodeDoc && decodeDoc.customerName &&
                                    (customerSupportedDocumentTest === null
                                        ?
                                        <MessageBar intent="info">
                                            <MessageBarBody>
                                                <MessageBarTitle>{decodeDoc.customerName} Can receive invoices (Test) check</MessageBarTitle>
                                                In progress
                                            </MessageBarBody>
                                        </MessageBar>
                                        :
                                        <MessageBar intent={customerSupportedDocumentTest && customerSupportedDocumentTest.canReceiveInvoices ? "success" : "error"} >
                                            <MessageBarBody>
                                                <MessageBarTitle>{decodeDoc.customerName} Can receive invoices (Test) check</MessageBarTitle>
                                            </MessageBarBody>
                                        </MessageBar>
                                    )
                                }
                                {UBLValidation &&
                                    <MessageBar intent="info">
                                        <MessageBarBody>
                                            <MessageBarTitle>UBL validation</MessageBarTitle>
                                            In progress
                                        </MessageBarBody>
                                    </MessageBar>
                                }
                                {UBLValidate && UBLValidate.currentSpecValidationResult &&
                                    < MessageBar intent={ValidationToMessageBarIntent(UBLValidate.currentSpecValidationResult.status)}
                                    >
                                        <MessageBarBody>
                                            <MessageBarTitle>UBL validation : {UBLValidate.currentSpecValidationResult.status}</MessageBarTitle>
                                            ({UBLValidate.currentSpecValidationResult.specVersion})
                                        </MessageBarBody>
                                    </MessageBar>
                                }
                                {UBLValidate && UBLValidate.currentSpecValidationResult && UBLValidate.currentSpecValidationResult.warnings &&
                                    UBLValidate.currentSpecValidationResult.warnings.map(w =>
                                        <MessageBar intent="warning">
                                            <MessageBarBody>
                                                <MessageBarTitle>{w.description}</MessageBarTitle>
                                            </MessageBarBody>
                                        </MessageBar>
                                    )
                                }
                                {UBLValidate && UBLValidate.currentSpecValidationResult && UBLValidate.currentSpecValidationResult.errors &&
                                    UBLValidate.currentSpecValidationResult.errors.map(w =>
                                        <MessageBar intent="error">
                                            <MessageBarBody>
                                                <MessageBarTitle>{w.description}</MessageBarTitle>
                                            </MessageBarBody>
                                        </MessageBar>
                                    )
                                }
                                {UBLValidate && UBLValidate.futureSpecValidationResult &&
                                    < MessageBar intent={ValidationToMessageBarIntent(UBLValidate.futureSpecValidationResult.status)}
                                    >
                                        <MessageBarBody>
                                            <MessageBarTitle>UBL validation : {UBLValidate.futureSpecValidationResult.status}</MessageBarTitle>
                                            ({UBLValidate.futureSpecValidationResult.specVersion})
                                        </MessageBarBody>
                                    </MessageBar>
                                }
                                {UBLValidate && UBLValidate.futureSpecValidationResult && UBLValidate.futureSpecValidationResult.warnings &&
                                    UBLValidate.futureSpecValidationResult.warnings.map(w =>
                                        <MessageBar intent="warning">
                                            <MessageBarBody>
                                                <MessageBarTitle>{w.description}</MessageBarTitle>
                                            </MessageBarBody>
                                        </MessageBar>
                                    )
                                }
                                {UBLValidate && UBLValidate.futureSpecValidationResult && UBLValidate.futureSpecValidationResult.errors &&
                                    UBLValidate.futureSpecValidationResult.errors.map(w =>
                                        <MessageBar intent="error">
                                            <MessageBarBody>
                                                <MessageBarTitle>{w.description}</MessageBarTitle>
                                            </MessageBarBody>
                                        </MessageBar>
                                    )
                                }
                                {invoiceSendUBL && invoiceSendUBL.peppolSendedDocs && invoiceSendUBL.peppolSendedDocs.length > 0 &&
                                    invoiceSendUBL.peppolSendedDocs.sort(SortHelper.peppolSendedDocs).map(ps =>
                                        <MessageBar intent="info">
                                            <MessageBarBody>
                                                <MessageBarTitle>Send {new Date(ps.peppoDocSendDate + "Z").toLocaleString()}</MessageBarTitle>
                                                {ps.sendStatusMessage}
                                            </MessageBarBody>
                                        </MessageBar>
                                    )
                                }
                            </form>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(dataActionCreators.requestInvoiceSendUBL(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    {viewDebugInfo &&
                        <Button
                            onClick={() => submit(true)}
                            type="submit"
                            form="my-form-id"
                            disabled={UBLValidation
                                || (!!UBLValidate && UBLValidate.currentSpecValidationResult.status !== "OK")
                                || (!customerSupportedDocumentTest || customerSupportedDocumentTest.canReceiveInvoices === false)
                            }
                        >
                            {t("Send") + " (Test)"}
                        </Button>
                    }
                    <Button
                        onClick={() => submit(false)}
                        type="submit"
                        form="my-form-id"
                        disabled={UBLValidation
                            || (!!UBLValidate && UBLValidate.currentSpecValidationResult.status !== "OK")
                            || (!customerSupportedDocumentProd || customerSupportedDocumentProd.canReceiveInvoices === false)
                            || (!viewDebugInfo && invoiceSendUBL !== null && invoiceSendUBL.peppolSent !== null)
                        }
                    >
                        {t("Send")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface >
    </Dialog >;
};